import { GOOGLE_BUSINESS_URL } from "@/data/googleReviews";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, Mail, Phone, MapPin, Loader2, Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useSmartHashNav } from "@/hooks/useSmartHashNav";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import GoogleRatingBadge from "@/components/GoogleRatingBadge";
import logoCabassol from "@/assets/logo-cabassol.webp";

export default function Footer() {
  const { t } = useLanguage();
  const { localePath } = useLocalizedPath();
  const { handleHashClick } = useSmartHashNav();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      toast.error((t as any).footer?.toasts?.invalidEmail || "Veuillez entrer une adresse email valide");
      return;
    }

    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .insert({ email: email.trim().toLowerCase() });

      if (error) {
        if (error.code === "23505") {
          toast.info((t as any).footer?.toasts?.alreadySubscribed || "Vous êtes déjà inscrit à notre newsletter !");
        } else {
          throw error;
        }
      } else {
        setIsSubscribed(true);
        toast.success((t as any).footer?.toasts?.thankYou || "Merci pour votre inscription !");
        setEmail("");
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      toast.error((t as any).footer?.toasts?.error || "Une erreur s'est produite. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  const footerLinks = [
    { name: t.nav.home, path: localePath("/") + "#hero", hash: "hero" },
    { name: t.nav.services, path: localePath("/services") },
    { name: t.nav.about, path: localePath("/about") },
    { name: (t.nav as any).faq || "FAQ", path: localePath("/") + "#faq", hash: "faq" },
    { name: (t.nav as any).reviews || "Avis", path: localePath("/avis") },
    { name: t.nav.blog || "Blog", path: localePath("/blog") },
    { name: t.nav.contact, path: localePath("/contact") },
  ];
  
  return (
    <footer className="bg-card text-card-foreground pt-16 pb-8 border-t">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1fr_1fr_1.2fr_0.8fr] gap-8 mb-12">
          <div className="animate-fade-in [animation-delay:100ms]">
            <a 
              href={localePath("/") + "#hero"}
              onClick={(e) => handleHashClick(e, "hero")}
              className="flex items-center gap-2 mb-4"
            >
              <img src={logoCabassol} alt="Conciergerie Cabassol" width="56" height="56" loading="lazy" decoding="async" style={{ aspectRatio: "1/1" }} className="block h-14 translate-y-0.5" />
              <div className="flex flex-col justify-center">
                <span className="text-xl font-display font-normal tracking-wide leading-snug text-foreground">
                  Conciergerie
                </span>
                <span className="text-xl font-display font-normal tracking-wide leading-snug text-foreground -mt-1">
                  Cabassol
                </span>
              </div>
            </a>
            <p className="text-muted-foreground mb-4">
              {t.footer.description}
            </p>
            <div className="flex items-center gap-4">
              <a href="https://www.instagram.com/conciergeriecabassol/" target="_blank" rel="noopener noreferrer" className="w-5 flex justify-center text-muted-foreground hover:text-gold transition-colors">
                <Instagram size={20} />
                <span className="sr-only">Instagram</span>
              </a>
              <a href="https://www.linkedin.com/company/conciergerie-cabassol/" target="_blank" rel="noopener noreferrer" className="w-5 flex justify-center text-muted-foreground hover:text-gold transition-colors">
                <Linkedin size={20} />
                <span className="sr-only">LinkedIn</span>
              </a>
              <a href="https://www.facebook.com/profile.php?id=61587492287335" target="_blank" rel="noopener noreferrer" className="w-5 flex justify-center text-muted-foreground hover:text-gold transition-colors">
                <Facebook size={20} />
                <span className="sr-only">Facebook</span>
              </a>
              <a href="https://www.tiktok.com/@conciergeriecabassol" target="_blank" rel="noopener noreferrer" className="w-5 flex justify-center text-muted-foreground hover:text-gold transition-colors" aria-label="TikTok Conciergerie Cabassol">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.92a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.35z"/>
                </svg>
                <span className="sr-only">TikTok</span>
              </a>
            </div>
            <div className="mt-4">
              <GoogleRatingBadge variant="compact" />
            </div>
          </div>
          
          <div className="animate-fade-in [animation-delay:200ms]">
            <h3 className="text-xl font-bold mb-4">{t.footer.quickLinks}</h3>
            <ul className="space-y-2">
              {footerLinks.map((link) => (
                <li key={link.name}>
                  {link.hash ? (
                    <a 
                      href={link.path}
                      onClick={(e) => handleHashClick(e, link.hash!)}
                      className="text-muted-foreground hover:text-gold transition-colors"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link 
                      to={link.path} 
                      className="text-muted-foreground hover:text-gold transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          
          <div className="animate-fade-in [animation-delay:300ms]">
            <h3 className="text-xl font-bold mb-4">{t.footer.contact}</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <MapPin className="w-5 h-5 mr-2 mt-0.5 text-gold" />
                <a
                  href={GOOGLE_BUSINESS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Voir la fiche Google de Conciergerie Cabassol"
                  className="text-muted-foreground hover:text-gold transition-colors"
                >
                  1 Traverse Notre-Dame<br />
                  13100 Aix-en-Provence<br />
                  <span className="text-gold text-sm underline underline-offset-4">Voir sur Google</span>
                </a>
              </li>
              <li className="flex items-center">
                <Phone className="w-5 h-5 mr-2 text-gold" />
                <a href="tel:+33659013155" className="text-muted-foreground hover:text-gold transition-colors">+33 6 59 01 31 55</a>
              </li>
              <li className="flex items-center overflow-hidden">
                <Mail className="w-5 h-5 mr-2 text-gold shrink-0" />
                <a href="mailto:eliott@conciergeriecabassol.fr" className="text-muted-foreground hover:text-gold transition-colors break-all">eliott@conciergeriecabassol.fr</a>
              </li>
            </ul>
          </div>
          
          <div className="animate-fade-in [animation-delay:400ms]">
            <h3 className="text-xl font-bold mb-4">{t.footer.newsletter}</h3>
            <p className="text-muted-foreground mb-4">
              {t.footer.newsletterDesc}
            </p>
            {isSubscribed ? (
              <div className="flex items-center gap-2 text-green-500">
                <Check className="w-5 h-5" />
                <span>{(t as any).footer?.toasts?.subscribed || "Inscrit avec succès !"}</span>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col space-y-2">
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.footer.yourEmail} 
                  className="rounded-md px-4 py-2 bg-muted text-foreground"
                  required 
                  disabled={isLoading}
                />
                <button 
                  type="submit" 
                  className="btn-primary mt-2 flex items-center justify-center gap-2"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t.footer.subscribe
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
        
        <div className="border-t border-border pt-8 mt-8 text-center text-muted-foreground">
          <p className="mb-2">&copy; {currentYear} Conciergerie Cabassol. {t.footer.allRights}</p>
          <Link to={localePath("/legal")} className="text-sm hover:text-gold transition-colors">
            {t.footer.legal} &amp; {t.footer.privacy}
          </Link>
        </div>
      </div>
    </footer>
  );
}
